/**
 * Amend Confirm Modal
 *
 * Confirmation modal for submitting study amendments
 */

import { useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import { Button } from '@/presentation/components/ui/Button'
import { VoiceDictationButton } from '@/presentation/components/VoiceDictation'

interface AmendConfirmModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  summary: {
    metadata: number
    instancesDeleted: number
    instancesAdded: number
    attachmentsDeleted: number
    attachmentsAdded: number
  }
  onConfirm: (amendmentNote: string) => void
  isLoading: boolean
}

export function AmendConfirmModal({
  open,
  onOpenChange,
  summary,
  onConfirm,
  isLoading,
}: AmendConfirmModalProps) {
  const [amendmentNote, setAmendmentNote] = useState('')
  const [error, setError] = useState('')

  const handleConfirm = () => {
    if (!amendmentNote.trim()) {
      setError('La nota de enmienda es requerida')
      return
    }
    onConfirm(amendmentNote)
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      setAmendmentNote('')
      setError('')
    }
    onOpenChange(isOpen)
  }

  const hasDeletes = summary.instancesDeleted > 0 || summary.attachmentsDeleted > 0

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Confirmar Enmienda</DialogTitle>
          <DialogDescription>Revise los cambios antes de guardar</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning for deletes */}
          {hasDeletes && (
            <div className="flex items-start gap-3 p-3 rounded-lg alert-warning">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Advertencia</p>
                <p>
                  {summary.instancesDeleted > 0 && `${summary.instancesDeleted} imagen(es) `}
                  {summary.instancesDeleted > 0 && summary.attachmentsDeleted > 0 && 'y '}
                  {summary.attachmentsDeleted > 0 && `${summary.attachmentsDeleted} adjunto(s) `}
                  seran eliminados permanentemente.
                </p>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="space-y-2 text-sm">
            <h4 className="font-medium">Resumen de cambios:</h4>
            <ul className="list-disc list-inside text-[var(--foreground-muted)] space-y-1">
              {summary.metadata > 0 && (
                <li>{summary.metadata} campo(s) de metadatos modificados</li>
              )}
              {summary.instancesDeleted > 0 && (
                <li className="text-red-600 dark:text-red-400">
                  {summary.instancesDeleted} imagen(es) a eliminar
                </li>
              )}
              {summary.instancesAdded > 0 && (
                <li className="text-green-600 dark:text-green-400">
                  {summary.instancesAdded} imagen(es) nueva(s)
                </li>
              )}
              {summary.attachmentsDeleted > 0 && (
                <li className="text-red-600 dark:text-red-400">
                  {summary.attachmentsDeleted} adjunto(s) a eliminar
                </li>
              )}
              {summary.attachmentsAdded > 0 && (
                <li className="text-green-600 dark:text-green-400">
                  {summary.attachmentsAdded} adjunto(s) nuevo(s)
                </li>
              )}
            </ul>
          </div>

          {/* Amendment Note */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">
                Nota de enmienda <span className="text-red-500">*</span>
              </label>
              <VoiceDictationButton
                onTranscript={(text) => setAmendmentNote((prev) => prev + ' ' + text)}
                disabled={isLoading}
              />
            </div>
            <textarea
              value={amendmentNote}
              onChange={(e) => {
                setAmendmentNote(e.target.value)
                if (error) setError('')
              }}
              placeholder="Explique los cambios realizados..."
              rows={3}
              className={`w-full rounded-md border ${
                error ? 'border-red-500' : 'border-[var(--border)]'
              } bg-[var(--background)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)]`}
            />
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>

          {/* Info about reassignment */}
          <div className="p-3 rounded-lg alert-info text-sm">
            Al guardar, el estudio sera reasignado automaticamente al radiologo que lo devolvio.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={isLoading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              'Confirmar Enmienda'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
